import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import { FiChevronRight } from "react-icons/fi";
import { DocumentTextIcon, InvoiceIcon } from "asset/icons";
import UploadDocModal from "component/form/uploadDocModal";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import axios from "axios";
import {
    setInvoice as setInvoiceUrl,
    setOtherDocument as setOtherDocumentUrl,
    setMemo,
} from "redux/slices/tradevuTransferSlice";
import {
    setCryptoInvoice,
    setCryptoSupportDocument,
    setCryptoNaration,
} from "redux/slices/cryptoTransferSlice";
import {
    setBankInvoice as setBankInvoiceUrl,
    setBankOtherDocument as setBankOtherDocumentUrl,
    setBankMemo as setBankMemo,
} from "redux/slices/bankTransferSlice";
import { setCryptoFiatMemo } from "redux/slices/cryptoFiatTransferSlice";

import {
    setCrossBorderMemo,
    setCrossBorderInvoice,
    setCrossBorderOtherDocument,
} from "redux/slices/crossBorderTransferSlice";

const PaymentInformation = ({ nextRoute }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const isBankRecipient = location.pathname.includes("bank-recipient");
    const isCryptoFlow = location.pathname.includes("crypto-wallet");
    const isCryptoToFiatFlow = location.pathname.includes("fiat-wallet");
    const isCrossBorder = location.pathname.includes("cross-border");

    const flowKey = isCrossBorder
        ? "crossBorder"
        : isCryptoFlow
        ? "crypto"
        : isCryptoToFiatFlow
        ? "cryptoFiat"
        : isBankRecipient
        ? "bank"
        : "internal";

    const storeBits = useSelector(
        (s) => ({
            // internal
            internalMemo: s.memo || "",
            internalInvName: s.invoice?.name || null,
            internalOtherName: s.otherDocument?.name || null,

            // bank
            bankMemo: s.recipientMemo || "",
            bankInvName: s.recipientInvoice?.name || null,
            bankOtherName: s.recipientOtherDocument?.name || null,

            // crypto
            cryptoMemo: s.cryptoTransfer?.naration || "",
            cryptoInvName: s.cryptoTransfer?.invoice?.name || null,
            cryptoOtherName: s.cryptoTransfer?.supportDocument?.name || null,

            // crypto-fiat (stored under recipient)
            cryptoFiatMemo: s.cryptoFiatTransfer?.recipient?.memo || "",
            cryptoFiatInvName: s.cryptoTransfer?.invoice?.name || null,
            cryptoFiatOtherName: s.cryptoTransfer?.supportDocument?.name || null,

            // cross-border
            crossBorderMemo: s.crossBorderTransfer?.memo || "",
            crossBorderInvName: s.crossBorderTransfer?.invoice?.name || null,
            crossBorderOtherName: s.crossBorderTransfer?.otherDocument?.name || null,
        }),
        shallowEqual
    );

    const initial = useMemo(() => {
        switch (flowKey) {
            case "crypto":
                return {
                    memo: storeBits.cryptoMemo,
                    inv: storeBits.cryptoInvName,
                    other: storeBits.cryptoOtherName,
                };
            case "cryptoFiat":
                return {
                    memo: storeBits.cryptoFiatMemo,
                    inv: storeBits.cryptoFiatInvName,
                    other: storeBits.cryptoFiatOtherName,
                };
            case "bank":
                return {
                    memo: storeBits.bankMemo,
                    inv: storeBits.bankInvName,
                    other: storeBits.bankOtherName,
                };
            case "crossBorder":
                return {
                    memo: storeBits.crossBorderMemo,
                    inv: storeBits.crossBorderInvName,
                    other: storeBits.crossBorderOtherName,
                };
            default:
                return {
                    memo: storeBits.internalMemo,
                    inv: storeBits.internalInvName,
                    other: storeBits.internalOtherName,
                };
        }
    }, [flowKey, storeBits]);

    const [memoInput, setMemoInput] = useState(initial.memo);
    const [invoiceLabel, setInvoiceLabel] = useState(initial.inv);
    const [otherLabel, setOtherLabel] = useState(initial.other);
    const [uploading, setUploading] = useState(false);
    const [modalType, setModalType] = useState(null);

    useEffect(() => {
        setInvoiceLabel(initial.inv || null);
        setOtherLabel(initial.other || null);
    }, [initial.inv, initial.other]);

    useEffect(() => {
        setMemoInput(initial.memo || "");
    }, [flowKey]);

    const closeModal = () => setModalType(null);

    const handleUpload = async (file, name) => {
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await axios.post("/v1/utility/upload-file", form);
            const link = res?.data?.link;
            if (!link) throw new Error("No link in response");

            closeModal();

            const fileBase =
                typeof file?.name === "string" ? file.name.replace(/\.[^/.]+$/, "") || null : null;
            const safeName =
                (name && name.trim()) ||
                fileBase ||
                (modalType === "invoice" ? "Invoice" : "Supporting document");

            if (modalType === "invoice") {
                setInvoiceLabel(safeName);
                if (isCrossBorder) {
                    dispatch(setCrossBorderInvoice({ url: link, name: safeName }));
                } else if (isCryptoFlow) {
                    dispatch(setCryptoInvoice({ url: link, name: safeName }));
                } else if (isBankRecipient) {
                    dispatch(setBankInvoiceUrl({ url: link, name: safeName }));
                } else {
                    dispatch(setInvoiceUrl({ url: link, name: safeName }));
                }
            } else {
                setOtherLabel(safeName);
                if (isCrossBorder) {
                    dispatch(setCrossBorderOtherDocument({ url: link, name: safeName }));
                } else if (isCryptoFlow) {
                    dispatch(setCryptoSupportDocument({ url: link, name: safeName }));
                } else if (isBankRecipient) {
                    dispatch(setBankOtherDocumentUrl({ url: link, name: safeName }));
                } else {
                    dispatch(setOtherDocumentUrl({ url: link, name: safeName }));
                }
            }
        } catch (err) {
            console.error("Upload error:", err);
        } finally {
            setUploading(false);
        }
    };

    const handleContinue = () => {
        if (isCrossBorder) {
            dispatch(setCrossBorderMemo(memoInput));
        } else if (isCryptoFlow) {
            dispatch(setCryptoNaration(memoInput));
        } else if (isCryptoToFiatFlow) {
            dispatch(setCryptoFiatMemo(memoInput));
        } else if (isBankRecipient) {
            dispatch(setBankMemo(memoInput));
        } else {
            dispatch(setMemo(memoInput));
        }

        navigate(nextRoute);
    };

    return (
        <OnboardingLayout title="Send money" showSkip={false}>
            <div className="max-w-3xl px-4 !md:px-0 md:mx-auto mt-10 space-y-10">
                <PaymentStepper currentStep={2} />

                <h2 className="text-2xl font-normal text-[#101828]">Enter payment information</h2>

                <div className="space-y-8">
                    {/* Memo */}
                    <div>
                        <label className="block text-sm text-[#101828] font-medium mb-2">
                            Add memo <span className="text-[#667085] font-normal">• Optional</span>
                        </label>
                        <textarea
                            placeholder="Write a note here"
                            className="w-full h-24 p-3 text-sm !shadow-none rounded-lg bg-[#DBDDF04D] resize-none outline-none"
                            value={memoInput}
                            onChange={(e) => setMemoInput(e.target.value)}
                            maxLength={200}
                        />
                    </div>

                    {/* Supporting docs */}
                    <div>
                        <p className="text-sm font-medium mb-2 text-[#101828]">
                            Supporting documents{" "}
                            <span className="text-[#667085] font-normal">• Optional</span>
                        </p>

                        <button
                            onClick={() => setModalType("invoice")}
                            className="flex justify-between items-center bg-white p-3 w-full"
                        >
                            <div className="flex items-center gap-2">
                                <InvoiceIcon className="text-[#9E77ED]" size={20} />
                                <span className="text-normal text-[#101828] font-normal">
                                    Invoice
                                </span>
                            </div>
                            <span className="text-sm text-[#667085]">
                                {invoiceLabel ?? "No invoice"}{" "}
                                <FiChevronRight className="inline-block ml-1" />
                            </span>
                        </button>

                        <button
                            onClick={() => setModalType("other")}
                            className="flex justify-between items-center bg-white p-3 w-full"
                        >
                            <div className="flex items-center gap-2">
                                <DocumentTextIcon className="text-[#9E77ED]" size={20} />
                                <span className="text-normal text-[#101828] font-normal">
                                    Other document
                                </span>
                            </div>
                            <span className="text-sm text-[#667085]">
                                {otherLabel ?? "No document"}{" "}
                                <FiChevronRight className="inline-block ml-1" />
                            </span>
                        </button>
                    </div>
                </div>

                {/* nav */}
                <div className="flex justify-end gap-6 items-center mt-6">
                    <button onClick={() => navigate(-1)} className="text-[#667085] font-medium">
                        Back
                    </button>
                    <button
                        onClick={handleContinue}
                        className="rounded-lg px-6 py-2 font-semibold text-white bg-[#6F00FF] hover:bg-[#5A00D6] transition"
                    >
                        Continue
                    </button>
                </div>
            </div>

            {modalType && (
                <UploadDocModal close={closeModal} uploadFunc={handleUpload} loading={uploading} />
            )}
        </OnboardingLayout>
    );
};

export default PaymentInformation;
